import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

/**
 * Código de 6 números (componentes.md §8, fuera de RV Campo): 6 cajas de 60 de alto, radio 16, `inputmode="numeric"` y
 * `autocomplete="one-time-code"`; pegar reparte los dígitos y al escribir se pasa a la siguiente.
 */
export function CodigoSeis({ valor, onChange, error = false, autoFocus = false, onCompleto }: { valor: string; onChange: (valor: string) => void; error?: boolean; autoFocus?: boolean; onCompleto?: (valor: string) => void }) {
    const cajas = useRef<(HTMLInputElement | null)[]>([]);
    const digitos = Array.from({ length: 6 }, (_, i) => valor.charAt(i));

    const fijar = (nuevo: string, foco: number) => {
        const limpio = nuevo.replace(/\D/g, '').slice(0, 6);
        onChange(limpio);
        cajas.current[Math.min(foco, 5)]?.focus();
        if (limpio.length === 6) {
            onCompleto?.(limpio);
        }
    };

    const escribe = (i: number, texto: string) => {
        const solo = texto.replace(/\D/g, '');
        if (solo.length > 1) {
            fijar(valor.slice(0, i) + solo, i + solo.length);
            return;
        }
        const lista = [...digitos];
        lista[i] = solo;
        fijar(lista.join('').slice(0, 6), solo === '' ? i : i + 1);
    };

    const tecla = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && digitos[i] === '' && i > 0) {
            e.preventDefault();
            fijar(valor.slice(0, i - 1), i - 1);
        } else if (e.key === 'ArrowLeft' && i > 0) {
            cajas.current[i - 1]?.focus();
        } else if (e.key === 'ArrowRight' && i < 5) {
            cajas.current[i + 1]?.focus();
        }
    };

    const pega = (i: number, e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const solo = e.clipboardData.getData('text').replace(/\D/g, '');
        fijar(valor.slice(0, i) + solo, i + solo.length);
    };

    return (
        <div role="group" aria-label="Código de 6 números" className={`rv-codigo${error ? ' rv-codigo--error' : ''}`}>
            {digitos.map((d, i) => (
                <input
                    // Las 6 cajas son fijas: su posición es su identidad.
                    key={i}
                    ref={(el) => {
                        cajas.current[i] = el;
                    }}
                    value={d}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    maxLength={6}
                    autoFocus={autoFocus && i === 0}
                    aria-label={`Número ${String(i + 1)} de 6`}
                    onChange={(e) => {
                        escribe(i, e.target.value);
                    }}
                    onKeyDown={(e) => {
                        tecla(i, e);
                    }}
                    onPaste={(e) => {
                        pega(i, e);
                    }}
                />
            ))}
        </div>
    );
}
